import { Observable } from "rxjs";
import { first, map, takeUntil } from "rxjs/operators";
import { Service } from "typedi";
import { FormState } from "../../application/core/models/constants/FormState";
import { IMessageBusEvent } from "../../application/core/models/IMessageBusEvent";
import { IStyles } from "../../shared/model/config/IStyles";

import { IframeFactory } from "../iframe-factory/IframeFactory";
import { DomMethods } from "../../application/core/shared/dom-methods/DomMethods";
import { MESSAGE_BUS } from "../../application/core/shared/message-bus/MessageBus";
import {
  ANIMATED_CARD_COMPONENT_IFRAME,
  ANIMATED_CARD_COMPONENT_NAME,
  CARD_NUMBER_COMPONENT_NAME,
  CARD_NUMBER_IFRAME,
  EXPIRATION_DATE_COMPONENT_NAME,
  EXPIRATION_DATE_IFRAME,
  SECURITY_CODE_COMPONENT_NAME,
  SECURITY_CODE_IFRAME,
  AFT_CUSTOMER_ACCOUNT_NUMBER_COMPONENT_NAME,
  AFT_CUSTOMER_ACCOUNT_NUMBER_IFRAME,
} from "../../application/core/models/constants/Selectors";

import { ofType } from "../../shared/services/message-bus/operators/ofType";
import { ConfigProvider } from "../../shared/services/config-provider/ConfigProvider";
import { IConfig } from "../../shared/model/config/IConfig";
import {
  PRIVATE_EVENTS,
  PUBLIC_EVENTS,
} from "../../application/core/models/constants/EventTypes";
import { Frame } from "../../application/core/shared/frame/Frame";
import { IMessageBus } from "../../application/core/shared/message-bus/IMessageBus";
import { JwtDecoder } from "../../shared/services/jwt-decoder/JwtDecoder";
import { Locale } from "../../application/core/shared/translator/Locale";
import { IComponentsIds } from "../../shared/model/config/IComponentsIds";
import { IStJwtPayload } from "../../application/core/models/IStJwtPayload";
import { EventScope } from "../../application/core/models/constants/EventScope";
import { PayButton } from "../pay-button/PayButton";
import { PayButtonFactory } from "../pay-button/PayButtonFactory";
import { IValidationMessageBus } from "../../application/core/models/IValidationMessageBus";
import { ICardPaymentFormUpdateMessageData } from "../../application/core/models/ICardPaymentFormUpdateMessageData";

export const CARDPAYMENTOPTION = "CARD";

@Service()
export class CardFrames {
  private animatedCard: HTMLIFrameElement;
  private cardNumber: HTMLIFrameElement;
  private expirationDate: HTMLIFrameElement;
  private securityCode: HTMLIFrameElement;
  private aftCustomerAccountNumber: HTMLIFrameElement;

  private defaultPaymentType: string;
  private paymentTypes: string[];
  private fieldsToSubmitLength: number;
  private noFieldConfiguration: boolean;
  private onlyCvvConfiguration: boolean;
  private configurationForStandardCard: boolean;
  private configurationWithAccountNumber: boolean;
  private configurationWithAccountNumberAndAnimatedCard: boolean;
  private loadAnimatedCard: boolean;

  private componentIds: IComponentsIds;
  private formId: string;
  private destroy$: Observable<void>;
  private styles: IStyles;
  /* @todo(typings) The problem with paymentTypes here is that it should be a string[], but if you replace that you will
     eventually find out that you need to pass string[] to URLSearchParams() in the IFrameFactory.create and apparently
     that function doesn't handle array of strings - so either we have bad typings here or we use it incorrectly. */

  private params: {
    locale: Locale;
    origin: string;
    paymentTypes?: any;
    defaultPaymentType?: string;
    isInputOptional?: string;
  };
  private elementsToRegister: HTMLElement[];
  private origin: string;
  private fieldsToSubmit: string[];
  private elementsTargets: string[];
  private payButton: PayButton;

  constructor(
    private configProvider: ConfigProvider,
    private iframeFactory: IframeFactory,
    private frame: Frame,
    private messageBus: IMessageBus,
    private jwtDecoder: JwtDecoder,
    private payButtonFactory: PayButtonFactory,
  ) {
    this.payButton = this.payButtonFactory.create();
  }

  init(): void {
    this.configProvider.getConfig$().subscribe((config: IConfig) => {
      this.fieldsToSubmit = config.fieldsToSubmit || [
        "pan",
        "expirydate",
        "securitycode",
      ];
      this.elementsToRegister = [];
      this.componentIds = config.componentIds;
      this.formId = config.formId;

      this.origin = config.origin;
      this.styles = this.getStyles(config.styles);
      this.params = {
        locale:
          this.jwtDecoder.decode<IStJwtPayload>(config.jwt).payload.locale ||
          "en_GB",
        origin: this.origin,
      };

      this.setInitValues(
        config.components.defaultPaymentType,
        config.components.paymentTypes,
        config.animatedCard,
        config.jwt,
        config.formId,
      );
      this.configureFormFieldsAmount(config.jwt);
      this.styles = this.getStyles(config.styles);
      this.destroy$ = this.messageBus.pipe(ofType(PUBLIC_EVENTS.DESTROY));
      this.payButton.init();
    });

    this.preventFormSubmit();
    this.initSubscribes();
    this.initCardFrames();
    this.elementsTargets = this.setElementsFields();
    this.registerElements(this.elementsToRegister, this.elementsTargets);
  }

  private isSecurityCodeInputOptional(): boolean {
    const elementSecurityCode = document.getElementById("st-security-code");
    if (!elementSecurityCode) {
      return false;
    }

    if (this.fieldsToSubmit && !this.fieldsToSubmit.includes("securitycode")) {
      return true;
    }

    const elementDataSet = elementSecurityCode.dataset;
    return elementDataSet && elementDataSet["stInputOptional"] === "true";
  }

  /* @todo(typings) Looks like this fn can take either IStyle or IStyles, hence the `if` inside. All the calls here
     are done with IStyles, but the tests for that class do not give enough confidence to assume so. */

  private getStyles(styles: any) {
    for (const key in styles) {
      if (styles[key] instanceof Object) {
        return styles;
      }
    }
    styles = { defaultStyles: styles };
    return styles;
  }

  private configureFormFieldsAmount(jwt: string): void {
    const onlyCvvNumberOfFields = 1;
    const completeFormNumberOfFields = 3;
    const completeFormWithAccountNumberFields = 4;

    this.fieldsToSubmitLength = this.fieldsToSubmit.length;
    this.noFieldConfiguration =
      this.fieldsToSubmitLength === onlyCvvNumberOfFields &&
      this.fieldsToSubmit.includes("securitycode");
    this.onlyCvvConfiguration =
      this.fieldsToSubmitLength === onlyCvvNumberOfFields &&
      this.fieldsToSubmit.includes("securitycode");
    this.configurationForStandardCard =
      this.fieldsToSubmitLength === completeFormNumberOfFields &&
      this.loadAnimatedCard &&
      this.fieldsToSubmit.includes("pan") &&
      this.fieldsToSubmit.includes("expirydate") &&
      this.fieldsToSubmit.includes("securitycode");
    this.configurationWithAccountNumber =
      this.fieldsToSubmitLength === completeFormWithAccountNumberFields &&
      !this.loadAnimatedCard && // Add this condition
      this.fieldsToSubmit.includes("pan") &&
      this.fieldsToSubmit.includes("expirydate") &&
      this.fieldsToSubmit.includes("securitycode") &&
      this.fieldsToSubmit.includes("customeraccountnumber");

    // Add new configuration for AFT with animated card
    this.configurationWithAccountNumberAndAnimatedCard =
      this.fieldsToSubmitLength === completeFormWithAccountNumberFields &&
      this.loadAnimatedCard &&
      this.fieldsToSubmit.includes("pan") &&
      this.fieldsToSubmit.includes("expirydate") &&
      this.fieldsToSubmit.includes("securitycode") &&
      this.fieldsToSubmit.includes("customeraccountnumber");
  }

  private registerElements(fields: HTMLElement[], targets: string[]): void {
    if (fields.length === targets.length) {
      targets.forEach((item, index) => {
        const element: HTMLElement = document.getElementById(item);
        if (element !== null) {
          element.appendChild(fields[index]);
        } else {
          // const { componentIds, components, fieldsToSubmit, formId, buttonId } =
          //   this.config;
          // //Errors are not recorded in AppMonitor if the startOnLoad configuration property is set to true.
          // //This is because card details are already passed via JWT if startOnLoad is set to true.
          // if (!this.config?.components?.startOnLoad) {
          //   this.internalMonitor.recordIssue(
          //     new Error(`
          //     ${item} is not found in the form. Form element misconfiguration
          //   `),
          //     {
          //       componentIds,
          //       components,
          //       fieldsToSubmit,
          //       formId,
          //       buttonId,
          //     }
          //   );
          // }
        }
      });

      this.destroy$.pipe(first()).subscribe(() => {
        targets.forEach((item, index) => {
          const element: HTMLElement = document.getElementById(item);
          const iframe: HTMLElement = fields[index];
          if (element && iframe && iframe.parentElement === element) {
            element.removeChild(iframe);
          }
        });
      });
    }
  }

  private setElementsFields(): string[] {
    if (this.configurationForStandardCard) {
      return [
        this.componentIds.cardNumber,
        this.componentIds.expirationDate,
        this.componentIds.securityCode,
        this.componentIds.animatedCard,
      ];
    } else if (this.configurationWithAccountNumberAndAnimatedCard) {
      return [
        this.componentIds.cardNumber,
        this.componentIds.expirationDate,
        this.componentIds.securityCode,
        this.componentIds.aftCustomerAccountNumber,
        this.componentIds.animatedCard,
      ];
    } else if (this.configurationWithAccountNumber) {
      return [
        this.componentIds.cardNumber,
        this.componentIds.expirationDate,
        this.componentIds.securityCode,
        this.componentIds.aftCustomerAccountNumber,
      ];
    } else if (this.onlyCvvConfiguration) {
      return [this.componentIds.securityCode];
    } else if (this.noFieldConfiguration) {
      return [];
    } else {
      return [
        this.componentIds.cardNumber,
        this.componentIds.expirationDate,
        this.componentIds.securityCode,
      ];
    }
  }

  private disableFormField(state: FormState, eventName: string): void {
    this.messageBus.publish({
      data: state,
      type: eventName,
    });
  }

  private initCardNumberFrame(styles: Record<string, string>): void {
    this.cardNumber = this.iframeFactory.create(
      CARD_NUMBER_COMPONENT_NAME,
      CARD_NUMBER_IFRAME,
      styles,
      this.params,
    );
    this.elementsToRegister.push(this.cardNumber);
  }

  private initExpiryDateFrame(styles: Record<string, string>): void {
    this.expirationDate = this.iframeFactory.create(
      EXPIRATION_DATE_COMPONENT_NAME,
      EXPIRATION_DATE_IFRAME,
      styles,
      this.params,
    );
    this.elementsToRegister.push(this.expirationDate);
  }

  private initSecurityCodeFrame(styles: Record<string, string>): void {
    const isOptional = this.isSecurityCodeInputOptional() ? "true" : undefined;

    this.securityCode = this.iframeFactory.create(
      SECURITY_CODE_COMPONENT_NAME,
      SECURITY_CODE_IFRAME,
      styles,
      {
        ...this.params,
        ...(isOptional !== undefined && { isInputOptional: isOptional }),
      },
    );

    this.elementsToRegister.push(this.securityCode);
  }

  private initAccountNumberFrame(styles: Record<string, string>): void {
    this.aftCustomerAccountNumber = this.iframeFactory.create(
      AFT_CUSTOMER_ACCOUNT_NUMBER_COMPONENT_NAME,
      AFT_CUSTOMER_ACCOUNT_NUMBER_IFRAME,
      styles,
      {
        ...this.params,
        ...{ isInputOptional: "false" },
      },
    );

    this.elementsToRegister.push(this.aftCustomerAccountNumber);
  }

  private initAnimatedCardFrame(): void {
    const animatedCardConfig = { ...this.params };
    if (this.paymentTypes !== undefined) {
      animatedCardConfig.paymentTypes = this.paymentTypes;
    }
    if (this.defaultPaymentType !== undefined) {
      animatedCardConfig.defaultPaymentType = this.defaultPaymentType;
    }
    this.animatedCard = this.iframeFactory.create(
      ANIMATED_CARD_COMPONENT_NAME,
      ANIMATED_CARD_COMPONENT_IFRAME,
      {},
      animatedCardConfig,
      -1,
    );
    this.elementsToRegister.push(this.animatedCard);
  }

  private initCardFrames(): void {
    const { defaultStyles } = this.styles;
    let { cardNumber, securityCode, expirationDate, aftCustomerAccountNumber } =
      this.styles;
    cardNumber = Object.assign({}, defaultStyles, cardNumber);
    securityCode = Object.assign({}, defaultStyles, securityCode);
    expirationDate = Object.assign({}, defaultStyles, expirationDate);
    aftCustomerAccountNumber = Object.assign(
      {},
      defaultStyles,
      aftCustomerAccountNumber,
    );

    if (this.onlyCvvConfiguration) {
      this.initSecurityCodeFrame(securityCode);
    } else if (this.configurationForStandardCard) {
      this.initCardNumberFrame(cardNumber);
      this.initExpiryDateFrame(expirationDate);
      this.initSecurityCodeFrame(securityCode);
      this.initAnimatedCardFrame();
    } else if (this.configurationWithAccountNumberAndAnimatedCard) {
      this.initCardNumberFrame(cardNumber);
      this.initExpiryDateFrame(expirationDate);
      this.initSecurityCodeFrame(securityCode);
      this.initAccountNumberFrame(aftCustomerAccountNumber);
      this.initAnimatedCardFrame();
    } else if (this.configurationWithAccountNumber) {
      this.initCardNumberFrame(cardNumber);
      this.initExpiryDateFrame(expirationDate);
      this.initSecurityCodeFrame(securityCode);
      this.initAccountNumberFrame(aftCustomerAccountNumber);
    } else {
      this.initCardNumberFrame(cardNumber);
      this.initExpiryDateFrame(expirationDate);
      this.initSecurityCodeFrame(securityCode);
    }
  }

  private initSubscribes(): void {
    this.subscribeFormRendered();
    this.submitFormListener();
    this.subscribeBlockSubmit();
    this.validateFieldsAfterSubmit();
    this.setMerchantInputListeners();
    this.subscribeFormValidationUpdated();
  }

  private subscribeFormRendered() {
    this.messageBus
      .pipe(
        ofType(PUBLIC_EVENTS.CARD_PAYMENT_FORM_RENDERED),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        try {
          const components = this.configProvider.getConfig()?.components;

          // Safely access config with optional chaining
          const formRenderedCallback =
            components?.callbacks?.onPaymentFormRendered;

          // Only call if it's actually a function
          if (typeof formRenderedCallback === "function") {
            // Wrap callback execution in try/catch to prevent errors from breaking the app
            try {
              formRenderedCallback();
            } catch (callbackError: unknown) {
              // ignore
            }
          }
        } catch (error) {
          // ignore
        }
      });
  }
  private subscribeFormValidationUpdated() {
    this.messageBus
      .pipe(
        ofType(PUBLIC_EVENTS.CARD_PAYMENT_FORM_UPDATED),
        takeUntil(this.destroy$),
      )
      .subscribe(
        (event: IMessageBusEvent<ICardPaymentFormUpdateMessageData>) => {
          try {
            const components = this.configProvider.getConfig()?.components;

            if (components?.startOnLoad === true) {
              // We don't need to trigger this callback in a startOnLoad scenario
              return;
            }

            // Safely access config with optional chaining
            const formValidityCallback =
              components?.callbacks?.onPaymentFormValidityChange;

            // Only call if it's actually a function
            if (typeof formValidityCallback === "function") {
              // Ensure data exists before accessing properties
              const isFormValid = event?.data?.isFormValid ?? false;
              const states = event?.data?.states;

              // Safely get field states with fallbacks
              const fieldsToSubmit = Array.isArray(this.fieldsToSubmit)
                ? this.fieldsToSubmit
                : [];

              // Build state object dynamically based on what's present in the event data
              const callbackState: any = {};

              // Only include fields that are present in the event data
              if (states?.cardNumber !== undefined) {
                callbackState.cardNumber = {
                  isValid: Boolean(states.cardNumber.isValid),
                  isOptional: !fieldsToSubmit.includes("pan"),
                };
              }

              if (states?.expirationDate !== undefined) {
                callbackState.expirationDate = {
                  isValid: Boolean(states.expirationDate.isValid),
                  isOptional: !fieldsToSubmit.includes("expirydate"),
                };
              }

              if (states?.securityCode !== undefined) {
                callbackState.securityCode = {
                  isValid: Boolean(states.securityCode.isValid),
                  isOptional: this.isSecurityCodeInputOptional(),
                };
              }

              if (states?.aftCustomerAccountNumber !== undefined) {
                callbackState.aftCustomerAccountNumber = {
                  isValid: Boolean(states.aftCustomerAccountNumber.isValid),
                  isOptional: !fieldsToSubmit.includes("customeraccountnumber"),
                };
              }

              // Wrap callback execution in try/catch to prevent errors from breaking the app
              try {
                formValidityCallback({
                  isFormValid: isFormValid,
                  fieldsToSubmit: fieldsToSubmit,
                  state: callbackState,
                });
              } catch (callbackError: unknown) {
                console.error(
                  "Error in onPaymentFormValidityChange callback:",
                  callbackError,
                );
              }
            }
          } catch (error) {
            console.error("Error processing form validation update:", error);
          }
        },
      );
  }
  private onInput(): void {
    const messageBusEvent: IMessageBusEvent = {
      data: DomMethods.parseForm(this.formId),
      type: MESSAGE_BUS.EVENTS_PUBLIC.UPDATE_MERCHANT_FIELDS,
    };
    this.messageBus.publish(messageBusEvent);
  }

  private beforePublishSubmitEvent(): void {
    const data: any = {
      /** @deprecated New clients should use 'name' instead, which is more consistent with other library callbacks. Note that the casing of the value will change */
      paymentOption: CARDPAYMENTOPTION,
      name: CARDPAYMENTOPTION,
    };

    if (this.isPaymentPrecheckEnabled(this.configProvider.getConfig())) {
      data.paymentStart = () => this.publishSubmitEvent();
    }

    this.messageBus.publish(
      {
        type: PUBLIC_EVENTS.PAYMENT_METHOD_PRE_CHECK,
        data,
      },
      EventScope.EXPOSE_TO_MERCHANT,
    );

    if (!data.paymentStart) {
      this.publishSubmitEvent();
    }
  }

  private publishSubmitEvent(): void {
    const messageBusEvent: IMessageBusEvent = {
      data: {
        fieldsToSubmit: this.fieldsToSubmit,
      },
      type: MESSAGE_BUS.EVENTS_PUBLIC.SUBMIT_FORM,
    };
    this.messageBus.publish(messageBusEvent, EventScope.EXPOSE_TO_MERCHANT);
  }

  private setMerchantInputListeners(): void {
    const els = DomMethods.getAllFormElements(
      document.getElementById(this.formId),
    );
    for (const el of els) {
      el.addEventListener("input", this.onInput.bind(this));
    }
  }

  private setInitValues(
    defaultPaymentType: string,
    paymentTypes: string[],
    loadAnimatedCard: boolean,
    jwt: string,
    formId: string,
  ): void {
    this.formId = formId;
    this.defaultPaymentType = defaultPaymentType;
    this.paymentTypes = paymentTypes;

    this.loadAnimatedCard =
      loadAnimatedCard !== undefined ? loadAnimatedCard : true;
  }

  private submitFormListener(): void {
    const clickHandler = () => this.beforePublishSubmitEvent();
    this.payButton.addClickHandler(clickHandler);

    this.destroy$.pipe(first()).subscribe(() => {
      this.payButton.removeClickHandler(clickHandler);
    });

    this.messageBus
      .pipe(ofType(PUBLIC_EVENTS.CALL_SUBMIT_EVENT), takeUntil(this.destroy$))
      .subscribe(() => this.beforePublishSubmitEvent());
  }

  private subscribeBlockSubmit(): void {
    this.messageBus
      .pipe(ofType(PUBLIC_EVENTS.SUBMIT_FORM), takeUntil(this.destroy$))
      .subscribe(() => this.payButton.disable(FormState.BLOCKED));

    this.messageBus
      .pipe(
        ofType(PUBLIC_EVENTS.BLOCK_FORM),
        map((event) => event.data),
        takeUntil(this.destroy$),
      )
      .subscribe((state: FormState) => {
        this.payButton.disable(state);
        this.disableFormField(
          state,
          MESSAGE_BUS.EVENTS_PUBLIC.BLOCK_CARD_NUMBER,
        );
        this.disableFormField(
          state,
          MESSAGE_BUS.EVENTS_PUBLIC.BLOCK_EXPIRATION_DATE,
        );
        this.disableFormField(
          state,
          MESSAGE_BUS.EVENTS_PUBLIC.BLOCK_SECURITY_CODE,
        );
        this.disableFormField(
          state,
          MESSAGE_BUS.EVENTS_PUBLIC.BLOCK_AFT_CUSTOMER_ACCOUNT_NUMBER,
        );
      });
  }

  private validateFieldsAfterSubmit(): void {
    this.messageBus
      .pipe(
        ofType(PRIVATE_EVENTS.VALIDATE_FORM),
        map((event) => event.data),
        takeUntil(this.destroy$),
      )
      .subscribe((data: IValidationMessageBus) => {
        const {
          cardNumber,
          expirationDate,
          securityCode,
          aftCustomerAccountNumber,
        } = data;
        if (!cardNumber.state) {
          this.publishValidatedFieldState(
            cardNumber,
            MESSAGE_BUS.EVENTS.VALIDATE_CARD_NUMBER_FIELD,
          );
        }
        if (!expirationDate.state) {
          this.publishValidatedFieldState(
            expirationDate,
            MESSAGE_BUS.EVENTS.VALIDATE_EXPIRATION_DATE_FIELD,
          );
        }
        if (!securityCode.state) {
          this.publishValidatedFieldState(
            securityCode,
            MESSAGE_BUS.EVENTS.VALIDATE_SECURITY_CODE_FIELD,
          );
        }
        if (aftCustomerAccountNumber && !aftCustomerAccountNumber.state) {
          this.publishValidatedFieldState(
            aftCustomerAccountNumber,
            MESSAGE_BUS.EVENTS.VALIDATE_AFT_CUSTOMER_ACCOUNT_NUMBER_FIELD,
          );
        }
      });
  }

  private publishValidatedFieldState(
    field: { message: string; state: boolean },
    eventType: string,
  ): void {
    const messageBusEvent: IMessageBusEvent<{ message: string }> = {
      data: { message: "" },
      type: "",
    };

    messageBusEvent.type = eventType;
    messageBusEvent.data.message = field.message;

    this.messageBus.publish(messageBusEvent);
  }

  private preventFormSubmit(): void {
    const preventFunction = (event: Event) => event.preventDefault();
    const paymentForm = document.getElementById(this.formId);

    paymentForm.addEventListener("submit", preventFunction);

    this.destroy$.pipe(first()).subscribe(() => {
      paymentForm.removeEventListener("submit", preventFunction);
    });
  }

  /**
   * A check to determine if the merchant has specified that
   * Card Payments should be configured to not auto-start.
   *
   * Auto-start gets disabled by adding ["CARD"] to disabledAutoPaymentStart.
   *
   * @param config The config.
   *
   * @returns
   *  True if the merchant wants control over the payment
   * start action, rather than having the code execute it
   * directly
   */
  private isPaymentPrecheckEnabled(config: IConfig): boolean {
    return (config.disabledAutoPaymentStart ?? [])
      .map((paymentMethodName) => paymentMethodName.toLowerCase())
      .includes(CARDPAYMENTOPTION.toLowerCase());
  }
}
